import * as React from 'react'
import { AlertTriangle } from 'lucide-react'
import { useUpdateVenueSchedule, useVenueTimezones } from '@/hooks/useVenues'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Facts } from '@/components/ui/facts'
import { Field } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { TimePicker } from '@/components/ui/time-picker'
import { FormError } from '@/components/ui/form-error'
import { updateVenueScheduleInputSchema } from '@lib/input/venue.input'
import { clockLabel } from '@/lib/eventTime'
import type { VenueDto } from '@lib/dto/venue.dto'

/** Which field a validation message belongs to, so `Field` can describe it. */
interface WindowErrors {
  timezone?: string
  startsAt?: string
  endsAt?: string
}

/**
 * The venue's clock and its usual evening.
 *
 * The clock is not for display — event times are wall clock and are shown
 * exactly as they were entered. It is what turns "23:00 on the 14th" into a real
 * instant, which is what deciding whether an event has finished needs. Until it
 * is set, this venue cannot answer that question, which is why an unset zone is
 * called out rather than left blank and quiet.
 *
 * See `docs/event-scheduling.md`.
 */
export function VenueSchedule({
  venue,
  canEdit,
}: {
  venue: VenueDto
  /** Edit adds changing the zone and the usual evening; without it they are read. */
  canEdit: boolean
}): React.ReactElement {
  // The venue page stays mounted when the next venue is already loaded, so a
  // form keyed only by the first venue would save that venue's hours onto this one.
  return canEdit ? <VenueScheduleForm key={venue.id} venue={venue} /> : <VenueScheduleFacts venue={venue} />
}

/** An unset zone, said where it matters: nothing can tell when this venue's events finish. */
function UnsetZoneNotice(): React.ReactElement {
  return (
    <div className="flex items-start gap-2 border border-line bg-surface-sunken p-3 text-sm">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
      <p className="text-ink-muted">
        This venue has no time zone yet, so nothing can work out when its events finish. Event
        times are still shown exactly as they are entered — the zone is only used to compare
        them to the actual time.
      </p>
    </div>
  )
}

/** The schedule for someone who can see it but not change it. */
function VenueScheduleFacts({ venue }: { venue: VenueDto }): React.ReactElement {
  return (
    <div className="space-y-6">
      {venue.timezone === null ? <UnsetZoneNotice /> : null}
      <Facts
        layout="stack"
        facts={[
          { label: 'Time zone', value: venue.timezone, empty: 'Not set' },
          { label: 'Usual start', value: clockLabel(venue.defaultStartsAt), empty: 'Not set' },
          { label: 'Usual end', value: clockLabel(venue.defaultEndsAt), empty: 'Not set' },
        ]}
      />
    </div>
  )
}

function VenueScheduleForm({ venue }: { venue: VenueDto }): React.ReactElement {
  const update = useUpdateVenueSchedule()
  // The list the API will accept, not one this browser computed: the two
  // runtimes read the same zone data but are upgraded at different times.
  const zones = useVenueTimezones()

  const [timezone, setTimezone] = React.useState(venue.timezone ?? '')
  const [startsAt, setStartsAt] = React.useState(venue.defaultStartsAt)
  const [endsAt, setEndsAt] = React.useState(venue.defaultEndsAt)
  const [errors, setErrors] = React.useState<WindowErrors>({})
  const [failure, setFailure] = React.useState<string | null>(null)

  const dirty =
    timezone !== (venue.timezone ?? '') ||
    startsAt !== venue.defaultStartsAt ||
    endsAt !== venue.defaultEndsAt

  /**
   * The venue's stored zone, kept selectable even if it is no longer offered.
   *
   * A zone set before the list narrowed is still this venue's zone, and a
   * dropdown that silently dropped it would show the wrong one as chosen and
   * overwrite it on the next save.
   */
  const offered = zones.data?.timezones ?? []
  const missingStoredZone = timezone !== '' && offered.length > 0 && !offered.includes(timezone)

  function save(): void {
    setFailure(null)
    // The same schema the API parses. A stored zone outside the allowlist is
    // still shown, and saving it is refused here rather than sent as a string
    // the write type does not accept. An empty select is "not set" (`null`).
    const parsed = updateVenueScheduleInputSchema.safeParse({
      timezone: timezone === '' ? null : timezone,
      defaultStartsAt: startsAt,
      defaultEndsAt: endsAt,
    })
    if (!parsed.success) {
      const next: WindowErrors = {}
      for (const issue of parsed.error.issues) {
        const field = issue.path[0]
        if (field === 'timezone') next.timezone = issue.message
        else if (field === 'defaultStartsAt') next.startsAt = issue.message
        else if (field === 'defaultEndsAt') next.endsAt = issue.message
      }
      setErrors(next)
      return
    }
    setErrors({})
    update.mutate(
      { id: venue.id, input: parsed.data },
      { onError: (cause) => setFailure(requestErrorMessage(cause)) },
    )
  }

  const zoneError = errors.timezone ?? (zones.isError ? 'Could not load the list of time zones.' : undefined)

  function zoneHint(): React.ReactNode {
    if (zones.isError) return 'The list of zones could not be loaded, so this cannot be changed.'
    if (missingStoredZone) return 'This venue’s zone is no longer offered; saving will replace it.'
    return "The clock the venue's staff work to."
  }

  return (
    <div className="space-y-6">
      {venue.timezone === null ? <UnsetZoneNotice /> : null}

      <Field
        label="Time zone"
        htmlFor="venue-tz"
        hint={zoneHint()}
        {...(zoneError === undefined ? {} : { error: zoneError })}
      >
        <Select
          id="venue-tz"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          disabled={zones.isPending || zones.isError}
        >
          <option value="">{zones.isPending ? 'Loading…' : 'Not set'}</option>
          {/* The stored zone first when the list no longer carries it, so what
              is saved is what is shown. */}
          {missingStoredZone ? <option value={timezone}>{timezone}</option> : null}
          {offered.map((zone) => (
            <option key={zone} value={zone}>
              {zone}
            </option>
          ))}
        </Select>
      </Field>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field
          label="Usual start"
          hint="Filled in when an inquiry is given a date."
          {...(errors.startsAt !== undefined ? { error: errors.startsAt } : {})}
        >
          <TimePicker value={startsAt} onChange={setStartsAt} ariaLabel="Usual start time" />
        </Field>
        <Field
          label="Usual end"
          {...(errors.endsAt !== undefined ? { error: errors.endsAt } : {})}
        >
          <TimePicker value={endsAt} onChange={setEndsAt} ariaLabel="Usual end time" />
        </Field>
      </div>

      {failure !== null ? (
        <FormError>
          {failure}
        </FormError>
      ) : null}

      <Button onClick={save} disabled={!dirty || update.isPending}>
        {update.isPending ? 'Saving…' : 'Save schedule settings'}
      </Button>
    </div>
  )
}
